/**
 * RackingInspectionActionButtons Component
 * Provides action buttons for Racking Inspection module
 * Similar to LeadershipWalkRedirectAction but for Racking Inspections
 */
import React, { useState } from "react";
import {
  View,
  StyleSheet,
  Text,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
} from "react-native";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { createApiClient, apiClient } from "../../../api/apiClient";
import { useToast } from "../../../components/LhToastContainer";
import { handleRackingInspectionErrorWithFriendlyMessage, RACKING_INSPECTION_API_NAMES } from '../../../store/utils/rackingInspectionErrorHandler';
// Import Material Icons directly from react-native-vector-icons for better web compatibility
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import FontFamily from "../../../constants/fonts";
import { Dimensions } from "react-native";
import { useTheme } from "../../../theme";

// Helper to detect iPad
const isIPad = Platform.OS === "ios" && Dimensions.get("window").width >= 768;

// Define __DEV__ if it doesn't exist (for web environments)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const __DEV__: boolean =
  typeof (globalThis as any).__DEV__ !== "undefined"
    ? (globalThis as any).__DEV__
    : process.env.NODE_ENV !== "production";

interface RackingInspectionActionButtonsProps {
  onSave: () => void;
  onComplete: () => void;
  inspectionId?: string | number; // ID of the inspection for PDF export
  walkStartedBy?: string; // ID of the user who started the walk
  location?: string; // Location name for PDF export
  status?: number; // 0 = in-progress, 1 = completed
  isReadOnly?: boolean;
  signatureRequired?: boolean;
  hasSignature?: boolean;
  disableActions?: boolean;
}

/**
 * Action buttons component for Racking Inspection module
 * Provides consistent design with Leadership Walk module
 */
const RackingInspectionActionButtons: React.FC<
  RackingInspectionActionButtonsProps
> = ({
  onSave,
  onComplete,
  inspectionId,
  walkStartedBy,
  location,
  status,
  isReadOnly = false,
  signatureRequired = true,
  hasSignature = false,
  disableActions = false,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPdfExporting, setIsPdfExporting] = useState(false);

  // Get user data from Redux store
  const userEmail = useSelector(
    (state: RootState) => state.auth?.user?.username
  );
  const selectedLocation = useSelector(
    (state: RootState) => state.appData.selectedLocation
  );
  const toast = useToast();

  // Check if the inspection is completed (status === 1)
  const isCompleted = status === 1 || isReadOnly;

  // Determine if save and complete buttons should be disabled
  const disableButtons =
    isSubmitting ||
    isReadOnly ||
    disableActions ||
    (signatureRequired && !hasSignature);

  // Check if PDF export should be disabled
  const isPdfExportDisabled = !inspectionId || !isCompleted;

  /**
   * Handle PDF export
   * Generates and emails a PDF report
   */
  const handlePdfExport = async () => {
    // Don't allow export if inspection is not completed or no ID is available
    if (isPdfExportDisabled || !userEmail) {
      const errorMessage = !userEmail
        ? "User email could not be retrieved. Please check your account settings."
        : !inspectionId
        ? "Please save your inspection first before exporting"
        : "PDF export is only available for completed inspections";

      toast.showToast({
        header: "PDF Export Unavailable",
        subtext: errorMessage,
        variant: "warning",
      });
      return;
    }

    setIsPdfExporting(true);
    try {
      console.log(
        "Exporting PDF for racking inspection:",
        inspectionId,
        "to:",
        userEmail
      );

      // Create a subject line for the email
      const locationName =
        selectedLocation?.name || selectedLocation?.displayText || "Unknown";
      const subjectLine = `Racking Inspection Report - ${locationName} - ${inspectionId}`;

      // Make the API call
      const api = createApiClient();
      const response = await apiClient(
        api,
        "/notifications/riseqa/email/pdf",
        "GET",
        undefined,
        {
          params: {
            emailTo: userEmail,
            location,
            inspection_module: "racking_inspection",
            createdBy: walkStartedBy,
            id: inspectionId,
            subject: subjectLine,
          },
        }
      );

      // Handle the response
      if (response && response.message) {
        toast.showToast({
          header: "Email Notification",
          subtext: response.message,
          variant: response.messageId ? "success" : "error",
        });
        console.log("PDF export success:", response.message);
      } else {
        toast.showToast({
          header: "Email Error",
          subtext: "Failed to send PDF export email",
          variant: "error",
        });
        throw new Error("Invalid response from server");
      }
    } catch (error: any) {
      console.error("Error exporting PDF:", error);
      try {
        handleRackingInspectionErrorWithFriendlyMessage(RACKING_INSPECTION_API_NAMES.DOWNLOAD_PDF, error, true, { inspectionId });
      } catch (handlerErr) {
        console.error('Failed to dispatch racking inspection centralized error for PDF export:', handlerErr);
      }
      toast.showToast({
        header: "Export Error",
        subtext: error?.message || "Failed to generate PDF report",
        variant: "error",
      });
    } finally {
      setIsPdfExporting(false);
    }
  };

  /**
   * Handle action button clicks
   * @param action Action type ("save" or "complete")
   */
  const handleAction = async (action: "save" | "complete") => {
    if (disableButtons) {
      return;
    }

    setIsSubmitting(true);
    try {
      if (action === "save") {
        await onSave();
      } else {
        await onComplete();
      }
    } catch (error: any) {
      console.error(`Error during ${action} action:`, error);
      try {
        handleRackingInspectionErrorWithFriendlyMessage(
          action === 'save' ? RACKING_INSPECTION_API_NAMES.UPDATE_INSPECTION : RACKING_INSPECTION_API_NAMES.UPDATE_INSPECTION,
          error,
          true,
          { inspectionId }
        );
      } catch (handlerErr) {
        console.error('Failed to dispatch racking inspection centralized error for action:', handlerErr);
      }
      // Show error toast to user
      toast.showToast({
        header: `${action === "save" ? "Save" : "Complete"} Error`,
        subtext:
          error?.message ||
          `An error occurred while trying to ${action} the inspection.`,
        variant: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <View style={[styles.actionRow, isDark && { borderTopColor: colors.border.default }]}>
      {/* PDF Export Button - separate on mobile, part of row on iPad/web */}
      <TouchableOpacity
        style={[
          isPdfExportDisabled
            ? styles.disabledButton
            : isCompleted
            ? styles.pdfExportButtonCompleted
            : styles.pdfExportButton,
          styles.pdfButtonContainer,
          Platform.OS === 'web' && !isPdfExportDisabled
            ? { cursor: 'pointer' as any }
            : {},
        ]}
        onPress={isPdfExportDisabled ? () => {} : handlePdfExport}
        disabled={isPdfExportDisabled}
        accessibilityState={{ disabled: isPdfExportDisabled }}
        accessibilityLabel={
          isPdfExportDisabled
            ? 'PDF Export Not Available'
            : isPdfExporting
            ? 'Sending PDF export email'
            : 'PDF Export'
        }
      >
        <View style={styles.buttonContent}>
          {isPdfExportDisabled ? (
            <>
              <MaterialIcons
                name='picture-as-pdf'
                size={24}
                color='#B1B1B1'
                style={{ marginRight: 8 }}
              />
              <Text style={styles.buttonTextDisabled}>PDF Export</Text>
            </>
          ) : isPdfExporting ? (
            <>
              <ActivityIndicator
                size='small'
                color={isCompleted ? '#034895' : '#FFF'}
                style={{ marginRight: 8 }}
              />
              <Text
                style={
                  isCompleted
                    ? styles.pdfExportButtonTextCompleted
                    : styles.buttonText
                }
              >
                Sending Email...
              </Text>
            </>
          ) : (
            <>
              <MaterialIcons
                name='picture-as-pdf'
                size={24}
                color={isCompleted ? '#034895' : 'white'}
                style={{ marginRight: 8 }}
              />
              <Text
                style={
                  isCompleted
                    ? styles.pdfExportButtonTextCompleted
                    : styles.buttonText
                }
              >
                PDF Export
              </Text>
            </>
          )}
        </View>
      </TouchableOpacity>

      {/* Save for Later Button */}
      <TouchableOpacity
        style={[
          styles.customButton,
          styles.saveButtonContainer,
          isSubmitting || disableButtons || isCompleted
            ? [styles.disabledButton, isDark && { backgroundColor: colors.background.elevated, borderColor: colors.border.default }]
            : [styles.saveButton, isDark && { backgroundColor: colors.background.elevated, borderColor: colors.brand.primary }],
          Platform.OS === 'web' &&
          !(isSubmitting || disableButtons || isCompleted)
            ? { cursor: 'pointer' as any }
            : {},
        ]}
        onPress={() => handleAction('save')}
        disabled={isSubmitting || disableButtons || isCompleted}
        accessibilityState={{
          disabled: isSubmitting || disableButtons || isCompleted,
        }}
        accessibilityLabel={
          isSubmitting
            ? 'Save for later (disabled during submission)'
            : isCompleted
            ? 'Save for later (disabled for completed inspection)'
            : 'Save for later'
        }
      >
        <View style={styles.buttonContent}>
          {isSubmitting ? (
            <ActivityIndicator
              size='small'
              color='#034895'
              style={{ marginRight: 8 }}
            />
          ) : (
            <>
              <MaterialIcons
                name='save'
                size={24}
                color={
                  isSubmitting || disableButtons || isCompleted
                    ? '#B1B1B1'
                    : '#034895'
                }
                style={{ marginRight: 8 }}
              />
              <Text
                style={
                  isSubmitting || disableButtons || isCompleted
                    ? styles.buttonTextDisabled
                    : [styles.saveButtonText, isDark && { color: colors.brand.primary }]
                }
              >
                Save for Later
              </Text>
            </>
          )}
        </View>
      </TouchableOpacity>

      {/* Complete Button */}
      <TouchableOpacity
        style={[
          styles.customButton,
          styles.completeButtonContainer,
          isSubmitting || disableButtons || isCompleted
            ? [styles.disabledButton, isDark && { backgroundColor: colors.background.elevated, borderColor: colors.border.default }]
            : [styles.completeButton, isDark && { backgroundColor: colors.brand.primary }],
          Platform.OS === 'web' &&
          !(isSubmitting || disableButtons || isCompleted)
            ? { cursor: 'pointer' as any }
            : {},
        ]}
        onPress={() => handleAction('complete')}
        disabled={isSubmitting || disableButtons || isCompleted}
        accessibilityState={{
          disabled: isSubmitting || disableButtons || isCompleted,
        }}
        accessibilityLabel={
          isSubmitting
            ? 'Complete form (disabled during submission)'
            : isCompleted
            ? 'Complete form (disabled for completed inspection)'
            : 'Complete form'
        }
      >
        <View style={styles.buttonContent}>
          {isSubmitting ? (
            <ActivityIndicator
              size='small'
              color='#B1B1B1'
              style={{ marginRight: 8 }}
            />
          ) : (
            <Text
              style={
                isSubmitting || disableButtons || isCompleted
                  ? styles.buttonTextDisabled
                  : styles.buttonText
              }
            >
              Complete
            </Text>
          )}
        </View>
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  actionRow: {
    flexDirection: Platform.OS === 'web' || isIPad ? 'row' : 'column',
    flexWrap: 'wrap',
    marginTop: 24,
    //paddingTop: 24,
    paddingBottom: 16,
    alignItems: Platform.OS === 'web' || isIPad ? 'flex-start' : 'stretch',
    justifyContent: Platform.OS === 'web' || isIPad ? 'flex-start' : 'center',
    width: '100%',
    gap: Platform.OS === 'web' || isIPad ? 20 : 8,
  },
  customButton: {
    paddingVertical: 8,
    paddingHorizontal: 24,
    borderRadius: 24,
    backgroundColor: '#034895',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1 as number,
    shadowRadius: 3,
    elevation: 2,
    minWidth: 120,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  saveButton: {
    backgroundColor: '#FFFFFF',
    borderRadius: 24,
    borderWidth: 1,
    borderColor: '#034895',
    paddingVertical: 8,
    paddingHorizontal: 24,
    height: 40,
    width: Platform.OS === 'web' || isIPad ? 189 : '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  completeButton: {
    backgroundColor: '#034895',
    borderRadius: 24,
    paddingVertical: 8,
    paddingHorizontal: 24,
    height: 40,
    width: Platform.OS === 'web' || isIPad ? 189 : '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  pdfExportButton: {
    backgroundColor: '#034895',
    borderRadius: 24,
    paddingVertical: 8,
    paddingHorizontal: 24,
    height: 40,
    width: Platform.OS === 'web' || isIPad ? 189 : '100%',
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1 as number,
    shadowRadius: 3,
    elevation: 2,
  },
  pdfExportButtonCompleted: {
    backgroundColor: '#FFFFFF',
    borderRadius: 24,
    paddingVertical: 8,
    paddingHorizontal: 24,
    height: 40,
    width: Platform.OS === 'web' || isIPad ? 189 : '100%',
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1 as number,
    shadowRadius: 3,
    elevation: 2,
    borderWidth: 1,
    borderColor: '#FFFFFF',
  },
  disabledButton: {
    opacity: 1,
    backgroundColor: '#F5F5F5',
    borderWidth: 1,
    borderColor: '#DADADA',
    borderRadius: 24,
    paddingVertical: 8,
    paddingHorizontal: 24,
    height: 40,
    width: Platform.OS === 'web' || isIPad ? 189 : '100%',
    shadowOpacity: 0.1 as number,
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web' ? { cursor: 'not-allowed' as any } : {}),
  },
  buttonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    gap: 8,
  },
  buttonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '700',
    fontFamily: FontFamily.regular,
    textAlign: 'center',
    lineHeight: 22,
    letterSpacing: 0,
  },
  pdfExportButtonTextCompleted: {
    color: '#034895',
    fontSize: 14,
    fontWeight: '700',
    fontFamily: FontFamily.regular,
    textAlign: 'center',
    lineHeight: 22,
    letterSpacing: 0,
  },
  saveButtonText: {
    color: '#034895',
    fontSize: 14,
    fontWeight: '700',
    fontFamily: FontFamily.regular,
    fontStyle: 'normal',
    textAlign: 'center',
    lineHeight: 22,
    letterSpacing: 0,
  },
  buttonTextDisabled: {
    color: '#B1B1B1',
    fontSize: 14,
    fontWeight: '700',
    fontFamily: FontFamily.regular,
    lineHeight: 22,
    letterSpacing: 0,
  },
  pdfButtonContainer: {
    width: Platform.OS === 'web' || isIPad ? 189 : '100%',
    marginBottom: Platform.OS === 'web' || isIPad ? 0 : 10,
  },
  saveButtonContainer: {
    width: Platform.OS === 'web' || isIPad ? 189 : '100%',
    marginBottom: Platform.OS === 'web' || isIPad ? 0 : 10,
  },
  completeButtonContainer: {
    width: Platform.OS === 'web' || isIPad ? 189 : '100%',
    marginBottom: Platform.OS === 'web' || isIPad ? 0 : 10,
  },
});

export default RackingInspectionActionButtons;
